// src/hooks/useTranscriptionHistory.js
import { useState, useEffect } from 'react';
import {
  collection, addDoc, getDocs, deleteDoc, updateDoc,
  doc, serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import {
  ref, uploadBytes, getDownloadURL, deleteObject,
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';

// Firestore rejects any write containing `undefined`. The summary comes back
// from the generateSummary Cloud Function and an occasionally-missing field
// (e.g. a topic detail) would otherwise fail the whole updateDoc. Deep-convert
// any undefined to null so the write always goes through.
const stripUndefined = (v) =>
  v === undefined ? null
  : Array.isArray(v) ? v.map(stripUndefined)
  : v && typeof v === 'object'
      ? Object.fromEntries(Object.entries(v).map(([k, val]) => [k, stripUndefined(val)]))
  : v;

export function useTranscriptionHistory() {
  const { user } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── Load history from Firestore ─────────────────────────────
  const loadHistory = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, 'users', user.uid, 'transcriptions'),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [user]);

  // ── Save a new transcription ────────────────────────────────
  // Two constraints shape this:
  //  1. Order: the transcript is written to Firestore FIRST; the audio upload
  //     is best-effort, so a Storage failure can't discard the transcript.
  //  2. Size: Firestore caps a document at 1 MiB. The word-level `words` array
  //     (per-word timestamps) is the thing that blows past that on long
  //     recordings, so it is NOT persisted — only text, utterances, duration,
  //     and a word count. The live session still holds the full transcript.
  const saveTranscription = async ({ file, transcript, language, source, videoTitle, thumbnail }) => {
    if (!user) return null;
    setSaving(true);

    try {
      const firstWords = transcript.text
        ? transcript.text.split(' ').slice(0, 6).join(' ') + '...'
        : null;
      const title = videoTitle || firstWords || 'Untitled Recording';
      const wordCount = transcript.words?.length || 0;

      // 1) Persist a lean transcript doc immediately (no word-level array).
      const docRef = await addDoc(
        collection(db, 'users', user.uid, 'transcriptions'),
        {
          title,
          language,
          source,
          audioUrl: null,
          storagePath: null,
          thumbnail: thumbnail || null,
          summary: null,
          wordCount,
          transcript: {
            text: transcript.text,
            // Keep only lightweight utterance fields. AssemblyAI nests a full
            // words[] array inside EACH utterance; stripping it (and the
            // top-level words[]) is what actually keeps the doc under 1 MiB.
            utterances: (transcript.utterances || []).map(u => ({
              speaker: u.speaker,
              text: u.text,
              start: u.start,
              end: u.end,
            })),
            audio_duration: transcript.audio_duration || null,
          },
          createdAt: serverTimestamp(),
        }
      );

      // 2) Best-effort audio upload. Failure here is logged, not fatal.
      let audioUrl = null;
      let storagePath = null;
      if (file) {
        try {
          const ext = file.name.split('.').pop() || 'mp4';
          storagePath = `recordings/${user.uid}/${Date.now()}.${ext}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, file);
          audioUrl = await getDownloadURL(storageRef);
          await updateDoc(docRef, { audioUrl, storagePath });
        } catch (uploadErr) {
          console.warn('Audio upload failed — transcript saved without playable audio:', uploadErr);
          audioUrl = null;
          storagePath = null;
        }
      }

      // Local entry keeps the FULL transcript (incl. words) for this session's view.
      const newEntry = {
        id: docRef.id,
        title,
        language,
        source,
        audioUrl,
        storagePath,
        thumbnail: thumbnail || null,
        summary: null,
        wordCount,
        transcript,
        createdAt: new Date(),
      };

      setHistory(prev => [newEntry, ...prev]);
      return newEntry;

    } catch (err) {
      console.error('Failed to save transcription:', err);
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ── Save summary to an existing transcription entry ─────────
  // Returns true on success, false on failure, so callers (e.g. the summary
  // modal's "saved" badge) can reflect what actually happened.
  const saveSummary = async (entryId, summary) => {
    if (!user || !entryId) return false;
    try {
      const cleanSummary = stripUndefined(summary);
      await updateDoc(
        doc(db, 'users', user.uid, 'transcriptions', entryId),
        { summary: cleanSummary }
      );
      setHistory(prev =>
        prev.map(h => h.id === entryId ? { ...h, summary: cleanSummary } : h)
      );
      return true;
    } catch (err) {
      console.error('Failed to save summary:', err);
      return false;
    }
  };

  // ── Delete a transcription ──────────────────────────────────
  const deleteTranscription = async (id, storagePath) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'users', user.uid, 'transcriptions', id));

      if (storagePath) {
        try {
          await deleteObject(ref(storage, storagePath));
        } catch {
          // File may already be gone, ignore
        }
      }

      setHistory(prev => prev.filter(h => h.id !== id));
    } catch (err) {
      console.error('Failed to delete transcription:', err);
    }
  };

  return { history, loading, saving, saveTranscription, saveSummary, deleteTranscription, loadHistory };
}